/**
 * Job Position Navigation Utility
 * 
 * Determines the current state/route for a job position based on its progress.
 * Supports both legacy flow (include_interviews/include_work_trial) and new stages-based flow.
 */

/**
 * Convert stage name to URL slug
 * @param {string} stageName - The stage name
 * @returns {string} URL-friendly slug
 */
export const stageNameToSlug = (stageName) => {
    if (!stageName) return '';
    return stageName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
};

/**
 * Convert URL slug back to find matching stage
 * @param {Array} stages - The stages array
 * @param {string} slug - The URL slug
 * @returns {Object|null} The matching stage or null
 */
export const findStageBySlug = (stages, slug) => {
    if (!stages || !Array.isArray(stages) || !slug) return null;
    
    return stages.find(stage => stageNameToSlug(stage.stage_name) === slug) || null;
};

/**
 * Check if job position has valid stages defined
 * @param {Object} position - The job position object
 * @returns {boolean} Whether stages are defined and valid
 */
export const hasValidStages = (position) => {
    if (!position?.stages) return false;
    const stages = position.stages;
    return Array.isArray(stages) && stages.length > 0;
};

/**
 * Get ordered stages array from position
 * @param {Object} position - The job position object
 * @returns {Array} Array of stages sorted by order
 */
export const getOrderedStages = (position) => {
    if (!hasValidStages(position)) return [];
    
    // Stages is already an array, just sort by order and add slug
    return [...position.stages]
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(stage => ({
            ...stage,
            slug: stageNameToSlug(stage.stage_name)
        }));
};

/**
 * Get stage path for a specific stage
 * @param {string} jobPositionId - The job position ID
 * @param {Object} stage - The stage object
 * @param {boolean} useLegacyUrls - Whether to use legacy URLs (for backward compatibility)
 * @returns {string} The route path
 */
export const getStageRoute = (jobPositionId, stage, useLegacyUrls = false) => {
    const baseRoute = `/job-position/${jobPositionId}`;
    
    if (useLegacyUrls && stage.special) {
        // Map special types to legacy URLs
        const legacyUrlMap = {
            'resume_screening': 'resume-screening',
            'interview_screening': 'interview-screening',
            'work_trial_screening': 'work-trial-screening'
        };
        const legacyPath = legacyUrlMap[stage.special];
        if (legacyPath) {
            return `${baseRoute}/${legacyPath}`;
        }
    }
    
    // For stages with no special or custom stages, use the stage slug
    const slug = stageNameToSlug(stage.stage_name);
    
    // Handle "Hire" stage specially - it always goes to /hire
    if (slug === 'hire' || stage.stage_name.toLowerCase() === 'hire') {
        return `${baseRoute}/hire`;
    }
    
    return `${baseRoute}/stage/${slug}`;
};

/**
 * Get the current route path for a job position based on its state.
 * Supports both legacy and stages-based navigation.
 * 
 * @param {Object} position - The job position object
 * @param {boolean} hasJobPosting - Whether the job position has an active job posting
 * @returns {string} The route path to navigate to
 */
export const getJobPositionCurrentRoute = (position, hasJobPosting = false) => {
    if (!position || !position.job_position_id) {
        return '/';
    }

    const baseRoute = `/job-position/${position.job_position_id}`;

    // Step 1: If no job posting exists, go to job posting page
    if (!hasJobPosting) {
        return `${baseRoute}/job-posting`;
    }

    // Check if this position uses the new stages system
    if (hasValidStages(position)) {
        const stages = getOrderedStages(position);
        
        // Find the first stage that is not completed
        for (const stage of stages) {
            // Skip "Hire" stage in progress check - it's the final destination
            if (stage.stage_name.toLowerCase() === 'hire' || stage.slug === 'hire') {
                continue;
            }
            
            if (!stage.selection_completed) {
                return getStageRoute(position.job_position_id, stage, false);
            }
        }
        
        // All stages completed, go to hire page (last stage)
        return `${baseRoute}/hire`;
    }

    // Legacy flow: use include_interviews and include_work_trial
    // Step 2: If resume screening is not completed, go to resume screening page
    if (!position.resume_screening_selection_completed) {
        return `${baseRoute}/resume-screening`;
    }

    // Step 3: If interviews are included and not completed, go to interview screening page
    if (position.include_interviews && !position.interview_screening_selection_completed) {
        return `${baseRoute}/interview-screening`;
    }

    // Step 4: If work trial is included and not completed, go to work trial screening page
    if (position.include_work_trial && !position.work_trial_selection_completed) {
        return `${baseRoute}/work-trial-screening`;
    }

    // Step 5: All stages completed, go to hire page
    return `${baseRoute}/hire`;
};

/**
 * Get the current stage name for a job position.
 * 
 * @param {Object} position - The job position object
 * @param {boolean} hasJobPosting - Whether the job position has an active job posting
 * @returns {string} The current stage name/slug
 */
export const getJobPositionCurrentStage = (position, hasJobPosting = false) => {
    if (!position) {
        return 'unknown';
    }

    if (!hasJobPosting) {
        return 'job-posting';
    }

    // Check if this position uses the new stages system
    if (hasValidStages(position)) {
        const stages = getOrderedStages(position);
        
        for (const stage of stages) {
            if (stage.stage_name.toLowerCase() === 'hire' || stage.slug === 'hire') {
                continue;
            }
            
            if (!stage.selection_completed) {
                return stage.slug;
            }
        }
        
        return 'hire';
    }

    // Legacy flow
    if (!position.resume_screening_selection_completed) {
        return 'resume-screening';
    }

    if (position.include_interviews && !position.interview_screening_selection_completed) {
        return 'interview-screening';
    }

    if (position.include_work_trial && !position.work_trial_selection_completed) {
        return 'work-trial-screening';
    }

    return 'hire';
};

/**
 * Navigate to the current state of a job position.
 * 
 * @param {Function} navigate - The react-router navigate function
 * @param {Object} position - The job position object
 * @param {boolean} hasJobPosting - Whether the job position has an active job posting
 */
export const navigateToJobPositionCurrentState = (navigate, position, hasJobPosting = false) => {
    const route = getJobPositionCurrentRoute(position, hasJobPosting);
    navigate(route);
};

/**
 * Get the candidate status filter for a stage based on its special type or stage name
 * @param {Object} stage - The stage object
 * @returns {string} The status to filter candidates by
 */
export const getStageStatusFilter = (stage) => {
    if (!stage) return null;
    
    // For special stages, use the predefined status values
    switch (stage.special) {
        case 'resume_screening':
            return 'resume-screening-stage';
        case 'interview_screening':
            return 'interview-stage';
        case 'work_trial_screening':
            return 'work-trial-stage';
        default:
            // For custom stages, convert stage name to status format
            // e.g., "Phone Interview" -> "phone-interview"
            // This allows filtering by custom stage statuses
            if (stage.stage_name) {
                const slug = stageNameToSlug(stage.stage_name);
                // Handle special case for "Hire" stage which uses "approved" status
                if (slug === 'hire') {
                    return 'approved';
                }
                return slug;
            }
            return null;
    }
};

/**
 * Get the first stage status for a job position (used for empty/null status normalization)
 * @param {Object} jobPosition - The job position object
 * @returns {string} The status value for the first stage
 */
export const getFirstStageStatus = (jobPosition) => {
    if (!hasValidStages(jobPosition)) {
        return 'resume-screening-stage';
    }
    
    const orderedStages = getOrderedStages(jobPosition);
    if (orderedStages.length > 0) {
        return getStageStatusFilter(orderedStages[0]);
    }
    
    return 'resume-screening-stage';
};

/**
 * Normalize candidate status - empty/null status should be treated as first stage
 * @param {string} status - The candidate's current status
 * @param {Object} jobPosition - The job position object
 * @returns {string} Normalized status value
 */
export const normalizeStatusForJobPosition = (status, jobPosition) => {
    if (!status || status === '') {
        return getFirstStageStatus(jobPosition);
    }
    return status;
};

/**
 * Get all statuses that should be visible on a particular stage page.
 * This includes the current stage status, all following stage statuses, and 
 * disapproved candidates on the first stage.
 * 
 * @param {Object} jobPosition - The job position object with stages
 * @param {Object} currentStage - The current stage object (or null for legacy)
 * @param {string} currentStageStatus - The status value for the current stage
 * @returns {Array<string>} Array of status values that should be visible
 */
export const getVisibleStatusesForStage = (jobPosition, currentStage, currentStageStatus) => {
    const visibleStatuses = [];
    
    if (!currentStageStatus) {
        return visibleStatuses;
    }
    
    // Always include the current stage status
    visibleStatuses.push(currentStageStatus);
    
    // Check if this is the first stage - if so, include disapproved
    const isFirstStage = currentStageStatus === getFirstStageStatus(jobPosition);
    if (isFirstStage) {
        visibleStatuses.push('disapproved');
    }
    
    if (!hasValidStages(jobPosition)) {
        // Legacy flow: determine following stages based on current stage
        const legacyOrder = ['resume-screening-stage', 'interview-stage', 'work-trial-stage', 'approved'];
        const currentIndex = legacyOrder.indexOf(currentStageStatus);
        
        if (currentIndex !== -1) {
            // Add all stages after the current one
            for (let i = currentIndex + 1; i < legacyOrder.length; i++) {
                // For legacy, check if interview/work-trial stages are enabled
                if (legacyOrder[i] === 'interview-stage' && !jobPosition?.include_interviews) {
                    continue;
                }
                if (legacyOrder[i] === 'work-trial-stage' && jobPosition?.include_work_trial === false) {
                    continue;
                }
                visibleStatuses.push(legacyOrder[i]);
            }
        }
        
        return visibleStatuses;
    }
    
    // For job positions with custom stages
    const orderedStages = getOrderedStages(jobPosition);
    
    // Find the current stage index
    let currentIndex = -1;
    for (let i = 0; i < orderedStages.length; i++) {
        const stageStatus = getStageStatusFilter(orderedStages[i]);
        if (stageStatus === currentStageStatus) {
            currentIndex = i;
            break;
        }
    }
    
    // Add all following stage statuses
    if (currentIndex !== -1) {
        for (let i = currentIndex + 1; i < orderedStages.length; i++) {
            const stageStatus = getStageStatusFilter(orderedStages[i]);
            if (stageStatus && !visibleStatuses.includes(stageStatus)) {
                visibleStatuses.push(stageStatus);
            }
        }
    }
    
    return visibleStatuses;
};

/**
 * Check if a candidate's status should be visible on a particular stage page.
 * 
 * @param {string} candidateStatus - The candidate's current status
 * @param {Object} jobPosition - The job position object
 * @param {Object} currentStage - The current stage object (or null for legacy)
 * @param {string} currentStageStatus - The status value for the current stage
 * @returns {boolean} True if the candidate should be visible on this stage page
 */
export const isCandidateVisibleOnStage = (candidateStatus, jobPosition, currentStage, currentStageStatus) => {
    // Normalize the candidate status (empty status = first stage)
    const normalizedStatus = normalizeStatusForJobPosition(candidateStatus, jobPosition);
    
    // Get all visible statuses for this stage
    const visibleStatuses = getVisibleStatusesForStage(jobPosition, currentStage, currentStageStatus);
    
    return visibleStatuses.includes(normalizedStatus);
};

/**
 * Get all available stage statuses from a job position's stages configuration.
 * Legacy positions only expose the stages they actually include
 * (include_interviews / include_work_trial); a null position returns the full
 * legacy set so consumers render sensibly while the position is still loading.
 * @param {Object} jobPosition - The job position object with stages
 * @returns {Array} Array of {status, label, slug, special, isSystemStatus} objects
 */
export const getAvailableStageStatuses = (jobPosition) => {
    if (!hasValidStages(jobPosition)) {
        return [
            { status: 'resume-screening-stage', label: 'Resume Screening', slug: 'resume-screening', isSystemStatus: false },
            ...(!jobPosition || jobPosition.include_interviews
                ? [{ status: 'interview-stage', label: 'Interview', slug: 'interview-screening', isSystemStatus: false }]
                : []),
            ...(!jobPosition || jobPosition.include_work_trial !== false
                ? [{ status: 'work-trial-stage', label: 'Work Trial', slug: 'work-trial-screening', isSystemStatus: false }]
                : []),
            { status: 'approved', label: 'Approved', slug: 'hire', isSystemStatus: false },
            { status: 'disapproved', label: 'Disapproved', slug: 'disapproved', isSystemStatus: true },
            { status: 'ban', label: 'Banned', slug: 'ban', isSystemStatus: true }
        ];
    }

    const orderedStages = getOrderedStages(jobPosition);
    const stageStatuses = orderedStages.map(stage => {
        const statusValue = getStageStatusFilter(stage);
        return {
            status: statusValue,
            label: stage.stage_name,
            slug: stage.slug,
            special: stage.special,
            isSystemStatus: false
        };
    });

    // Add system statuses that aren't stage-specific
    stageStatuses.push(
        { status: 'disapproved', label: 'Disapproved', slug: 'disapproved', isSystemStatus: true },
        { status: 'ban', label: 'Banned', slug: 'ban', isSystemStatus: true }
    );

    return stageStatuses;
};

/**
 * Get status display label from job position stages configuration
 * @param {string} status - The status value
 * @param {Object} jobPosition - The job position object
 * @returns {string} Display label for the status
 */
export const getStatusDisplayLabel = (status, jobPosition) => {
    if (!status) return 'Pending';
    
    // Check custom stages first
    if (hasValidStages(jobPosition)) {
        const orderedStages = getOrderedStages(jobPosition);
        for (const stage of orderedStages) {
            const stageStatus = getStageStatusFilter(stage);
            if (status === stageStatus) {
                return stage.stage_name;
            }
        }
    }
    
    // Fallback to standard status labels
    const statusLabels = {
        'resume-screening-stage': 'Resume Screening',
        'interview-stage': 'Interview',
        'work-trial-stage': 'Work Trial',
        'approved': 'Approved',
        'disapproved': 'Disapproved',
        'ban': 'Banned'
    };
    
    if (statusLabels[status]) {
        return statusLabels[status];
    }
    
    // Final fallback: convert slug to title case
    return status.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

/**
 * Get status badge configuration (color, background) for a status
 * @param {string} status - The status value  
 * @param {Object} jobPosition - The job position object (optional)
 * @returns {Object} Badge config with bg, color, and label properties
 */
export const getStatusBadgeConfig = (status, jobPosition = null) => {
    // Get display label
    const label = getStatusDisplayLabel(status, jobPosition);
    
    // System statuses have fixed colors
    const systemColors = {
        'approved': { bg: '#dcfce7', color: '#16a34a' },
        'disapproved': { bg: '#fee2e2', color: '#dc2626' },
        'ban': { bg: '#fecaca', color: '#b91c1c' }
    };
    
    if (systemColors[status]) {
        return { ...systemColors[status], label };
    }
    
    // Check for special stage types
    if (hasValidStages(jobPosition)) {
        const orderedStages = getOrderedStages(jobPosition);
        for (const stage of orderedStages) {
            const stageStatus = getStageStatusFilter(stage);
            if (status === stageStatus) {
                // Color based on special type or stage order
                if (stage.special === 'resume_screening') {
                    return { bg: '#f3e8ff', color: '#9333ea', label };
                }
                if (stage.special === 'interview_screening') {
                    return { bg: '#dbeafe', color: '#2563eb', label };
                }
                if (stage.special === 'work_trial_screening') {
                    return { bg: '#fef3c7', color: '#d97706', label };
                }
                // For hire/approved stage
                if (stage.slug === 'hire' || stage.stage_name.toLowerCase() === 'hire') {
                    return { bg: '#dcfce7', color: '#16a34a', label };
                }
                // Custom stages get a teal/cyan color scheme
                return { bg: '#e0f2fe', color: '#0284c7', label };
            }
        }
    }
    
    // Legacy status colors
    const legacyColors = {
        'resume-screening-stage': { bg: '#f3e8ff', color: '#9333ea' },
        'interview-stage': { bg: '#dbeafe', color: '#2563eb' },
        'work-trial-stage': { bg: '#fef3c7', color: '#d97706' }
    };
    
    if (legacyColors[status]) {
        return { ...legacyColors[status], label };
    }
    
    // Default color for unknown status
    return { bg: '#f1f5f9', color: '#64748b', label };
};

/**
 * Get the next stage information for a given current status
 * @param {string} currentStatus - The current status value
 * @param {Object} jobPosition - The job position object
 * @returns {Object|null} Next stage info {status, label, special, stage} or null if at final stage
 */
export const getNextStageInfo = (currentStatus, jobPosition) => {
    if (!hasValidStages(jobPosition)) {
        // Legacy flow: resume-screening -> interview/work-trial -> approved
        const legacyFlow = {
            'resume-screening-stage': jobPosition?.include_interviews 
                ? { status: 'interview-stage', label: 'Interview Stage', special: null }
                : { status: 'work-trial-stage', label: 'Work Trial Stage', special: null },
            'interview-stage': { status: 'work-trial-stage', label: 'Work Trial Stage', special: null },
            'work-trial-stage': { status: 'approved', label: 'Approved', special: null }
        };
        return legacyFlow[currentStatus] || null;
    }
    
    const orderedStages = getOrderedStages(jobPosition);
    
    // Normalize current status
    const normalizedStatus = normalizeStatusForJobPosition(currentStatus, jobPosition);
    
    // Find current stage index
    let currentIndex = -1;
    for (let i = 0; i < orderedStages.length; i++) {
        const stageStatus = getStageStatusFilter(orderedStages[i]);
        if (stageStatus === normalizedStatus) {
            currentIndex = i;
            break;
        }
    }
    
    // If not found or at last stage, return null
    if (currentIndex === -1 || currentIndex >= orderedStages.length - 1) {
        return null;
    }
    
    // Get next stage
    const nextStage = orderedStages[currentIndex + 1];
    return {
        status: getStageStatusFilter(nextStage),
        label: nextStage.stage_name,
        special: nextStage.special,
        stage: nextStage
    };
};

/**
 * Determines whether to use the new advance-to-next-stage endpoint or legacy status update
 * @param {Object} jobPosition - The job position object
 * @returns {boolean} True if should use advance endpoint, false for legacy
 */
export const shouldUseAdvanceEndpoint = (jobPosition) => {
    return hasValidStages(jobPosition);
};

/**
 * Get the legacy next status for a given current status (for job positions without custom stages)
 * @param {string} currentStatus - Current status
 * @param {Object} jobPosition - Job position object
 * @returns {string|null} Next status or null
 */
export const getLegacyNextStatus = (currentStatus, jobPosition) => {
    // For resume screening, next is either interview or work-trial based on job position config
    if (currentStatus === 'resume-screening-stage' || !currentStatus) {
        return jobPosition?.include_interviews ? 'interview-stage' : 'work-trial-stage';
    }
    // For interview stage, next is work-trial
    if (currentStatus === 'interview-stage') {
        return jobPosition?.include_work_trial ? 'work-trial-stage' : 'approved';
    }
    // For work trial stage, next is approved
    if (currentStatus === 'work-trial-stage') {
        return 'approved';
    }
    return null;
};

/**
 * Get the next stage status for advancing a candidate from the current stage
 * @param {Object} jobPosition - The job position object with stages
 * @param {Object} currentStage - The current stage object
 * @returns {string|null} The status to set for the next stage, or null if at final stage
 */
export const getNextStageStatus = (jobPosition, currentStage) => {
    if (!currentStage) return null;
    
    if (!hasValidStages(jobPosition)) {
        // Use legacy flow
        const currentStatus = getStageStatusFilter(currentStage);
        return getLegacyNextStatus(currentStatus, jobPosition);
    }
    
    const orderedStages = getOrderedStages(jobPosition);
    
    // Find current stage by comparing order or slug
    const currentIndex = orderedStages.findIndex(s => 
        s.order === currentStage.order || 
        stageNameToSlug(s.stage_name) === stageNameToSlug(currentStage.stage_name)
    );
    
    // If not found or at last stage
    if (currentIndex === -1 || currentIndex >= orderedStages.length - 1) {
        // At final stage, return 'approved'
        return 'approved';
    }
    
    // Get next stage and return its status filter
    const nextStage = orderedStages[currentIndex + 1];
    return getStageStatusFilter(nextStage);
};

/**
 * Get display configuration for a stage
 * @param {Object} stage - The stage object
 * @returns {Object} Display configuration with title, subtitle, icon, etc.
 */
export const getStageDisplayConfig = (stage) => {
    if (!stage) {
        return {
            title: 'Stage',
            subtitle: 'Manage candidates in this stage',
            icon: 'default'
        };
    }

    // Default config based on special type
    const specialConfigs = {
        'resume_screening': {
            title: stage.stage_name || 'Resume Screening',
            subtitle: 'Review and screen candidate resumes',
            icon: 'resume'
        },
        'interview_screening': {
            title: stage.stage_name || 'Interview Screening',
            subtitle: 'Conduct and evaluate candidate interviews',
            icon: 'interview'
        },
        'work_trial_screening': {
            title: stage.stage_name || 'Work Trial Screening',
            subtitle: 'Evaluate work trial submissions',
            icon: 'work-trial'
        }
    };

    if (stage.special && specialConfigs[stage.special]) {
        return specialConfigs[stage.special];
    }

    // For custom stages
    return {
        title: stage.stage_name || 'Custom Stage',
        subtitle: `Manage candidates in ${stage.stage_name || 'this stage'}`,
        icon: 'custom'
    };
};

/**
 * Determine if the new stage-based finish selection endpoint should be used.
 * This is true if the job position has custom stages defined.
 * @param {Object} jobPosition - The job position object.
 * @returns {boolean} True if the stage-based endpoint should be used.
 */
export const shouldUseStageFinishEndpoint = (jobPosition) => {
    return hasValidStages(jobPosition);
};

/**
 * Get the stage slug for a stage in the pipeline.
 * For special stages, this returns the appropriate slug based on the special type.
 * @param {Object} stage - The stage object.
 * @returns {string|null} The stage slug or null if invalid.
 */
export const getStageSlug = (stage) => {
    if (!stage || !stage.stage_name) return null;
    return stageNameToSlug(stage.stage_name);
};

/**
 * Find a stage by its special type in the job position's stages array.
 * @param {Object} jobPosition - The job position object.
 * @param {string} specialType - The special type ('resume_screening', 'interview_screening', 'work_trial_screening').
 * @returns {Object|null} The matching stage or null.
 */
export const findStageBySpecialType = (jobPosition, specialType) => {
    if (!hasValidStages(jobPosition) || !specialType) return null;
    return jobPosition.stages.find(stage => stage.special === specialType) || null;
};

/**
 * Check if a stage is completed.
 * @param {Object} stage - The stage object.
 * @returns {boolean} True if the stage is completed.
 */
export const isStageCompleted = (stage) => {
    if (!stage) return false;
    return stage.selection_completed === true;
};

/**
 * Get the current (first incomplete) stage for a job position.
 * The current stage is the first stage (by order) that has selection_completed = false.
 * @param {Object} jobPosition - The job position object.
 * @returns {Object|null} The current stage or null if all completed or no stages.
 */
export const getCurrentStage = (jobPosition) => {
    if (!hasValidStages(jobPosition)) return null;
    
    const orderedStages = getOrderedStages(jobPosition);
    
    // Find the first stage that is not completed
    for (const stage of orderedStages) {
        // Skip "Hire" stage in progress check - it's the final destination
        if (stage.stage_name?.toLowerCase() === 'hire' || stage.slug === 'hire') {
            continue;
        }
        
        if (!stage.selection_completed) {
            return stage;
        }
    }
    
    // All stages are completed, return null
    return null;
};

/**
 * Check if all stages before a specific stage are completed.
 * This helps determine if a stage can be marked as complete.
 * @param {Object} jobPosition - The job position object.
 * @param {Object} targetStage - The target stage to check.
 * @returns {boolean} True if all previous stages are completed.
 */
export const arePreviousStagesCompleted = (jobPosition, targetStage) => {
    if (!hasValidStages(jobPosition) || !targetStage) return false;
    
    const orderedStages = getOrderedStages(jobPosition);
    const targetIndex = orderedStages.findIndex(
        s => stageNameToSlug(s.stage_name) === stageNameToSlug(targetStage.stage_name)
    );
    
    if (targetIndex <= 0) return true; // First stage or not found
    
    // Check all stages before the target
    for (let i = 0; i < targetIndex; i++) {
        const stage = orderedStages[i];
        // Skip "Hire" stage
        if (stage.stage_name?.toLowerCase() === 'hire' || stage.slug === 'hire') {
            continue;
        }
        if (!stage.selection_completed) {
            return false;
        }
    }
    
    return true;
};

/**
 * Get the route to the next stage after completing a stage.
 * @param {string} jobPositionId - The job position ID.
 * @param {Object} jobPosition - The job position object.
 * @param {Object} currentStage - The current stage that was just completed.
 * @returns {string|null} The route to the next stage, or null if no next stage.
 */
export const getNextStageRoute = (jobPositionId, jobPosition, currentStage) => {
    if (!jobPositionId) return null;
    
    const baseRoute = `/job-position/${jobPositionId}`;
    
    if (!hasValidStages(jobPosition) || !currentStage) {
        // Legacy flow: determine next stage based on special type
        if (currentStage?.special === 'resume_screening') {
            return jobPosition?.include_interviews 
                ? `${baseRoute}/interview-screening`
                : (jobPosition?.include_work_trial !== false ? `${baseRoute}/work-trial-screening` : `${baseRoute}/hire`);
        }
        if (currentStage?.special === 'interview_screening') {
            return jobPosition?.include_work_trial !== false 
                ? `${baseRoute}/work-trial-screening` 
                : `${baseRoute}/hire`;
        }
        if (currentStage?.special === 'work_trial_screening') {
            return `${baseRoute}/hire`;
        }
        return null;
    }
    
    const orderedStages = getOrderedStages(jobPosition);
    const currentSlug = stageNameToSlug(currentStage.stage_name);
    const currentIndex = orderedStages.findIndex(s => s.slug === currentSlug);
    
    if (currentIndex === -1 || currentIndex >= orderedStages.length - 1) {
        // Current stage not found or is the last stage, go to hire
        return `${baseRoute}/hire`;
    }
    
    // Get next stage
    const nextStage = orderedStages[currentIndex + 1];
    
    // If next stage is "Hire", go to hire page
    if (nextStage.stage_name?.toLowerCase() === 'hire' || nextStage.slug === 'hire') {
        return `${baseRoute}/hire`;
    }
    
    // Otherwise, go to the next stage
    return `${baseRoute}/stage/${nextStage.slug}`;
};

/**
 * Get stage names for filtering candidates (current stage + all subsequent stages)
 * This is used to call the candidates-by-stages endpoint.
 * Also includes empty string to catch candidates with no current_stage (defaults to first stage).
 * 
 * @param {Object} jobPosition - The job position object
 * @param {Object} currentStage - The current stage object
 * @returns {string[]} Array of stage names to filter by
 */
export const getStageNamesForFiltering = (jobPosition, currentStage) => {
    if (!hasValidStages(jobPosition) || !currentStage) {
        return [];
    }
    
    const orderedStages = getOrderedStages(jobPosition);
    const stageNames = [];
    
    // Find current stage index
    const currentIndex = orderedStages.findIndex(s => 
        s.order === currentStage.order || 
        stageNameToSlug(s.stage_name) === stageNameToSlug(currentStage.stage_name)
    );
    
    if (currentIndex === -1) {
        // Stage not found, just return the current stage name
        if (currentStage.stage_name) {
            stageNames.push(currentStage.stage_name);
        }
        return stageNames;
    }
    
    // Add current stage and all subsequent stages
    for (let i = currentIndex; i < orderedStages.length; i++) {
        if (orderedStages[i].stage_name) {
            stageNames.push(orderedStages[i].stage_name);
        }
    }
    
    // If this is the first stage, also include empty string to catch candidates with no current_stage
    if (currentIndex === 0) {
        stageNames.push('');
    }
    
    return stageNames;
};

/**
 * Get the first stage name from a job position
 * @param {Object} jobPosition - The job position object
 * @returns {string|null} The first stage name or null
 */
export const getFirstStageName = (jobPosition) => {
    if (!hasValidStages(jobPosition)) return null;
    const orderedStages = getOrderedStages(jobPosition);
    return orderedStages.length > 0 ? orderedStages[0].stage_name : null;
};

/**
 * Resolve the effective filter status for an application, handling both
 * legacy (`app.status`) and custom-staged (`candidate.current_stage`) models.
 *
 * For custom-staged positions the priority is:
 *   1. Decision statuses on the candidate (approved / disapproved / ban)
 *   2. The candidate's `current_stage` mapped back to its stage-filter slug
 *   3. Fall back to normalizing `app.status` (legacy behaviour)
 *
 * @param {string} appStatus       - The application-level status field
 * @param {Object} candidate       - The candidate object (may be undefined)
 * @param {Object} jobPosition     - The job position object
 * @returns {string} A status value comparable to `getAvailableStageStatuses` entries
 */
export const getEffectiveStatus = (appStatus, candidate, jobPosition) => {
    if (hasValidStages(jobPosition)) {
        const DECISION_STATUSES = ['approved', 'disapproved', 'ban'];
        if (candidate?.status && DECISION_STATUSES.includes(candidate.status)) {
            return candidate.status;
        }

        if (candidate?.current_stage) {
            const orderedStages = getOrderedStages(jobPosition);
            const matchingStage = orderedStages.find(
                s => s.stage_name === candidate.current_stage
            );
            if (matchingStage) {
                return getStageStatusFilter(matchingStage);
            }
        }
    }

    return normalizeStatusForJobPosition(appStatus, jobPosition);
};

/** Decision statuses — on custom-staged positions these live on candidate.status,
 *  orthogonal to the pipeline stage (candidate.current_stage). */
export const DECISION_STATUSES = ['approved', 'disapproved', 'ban'];

/**
 * Resolve which board stage column a candidate belongs to.
 *
 * Custom-staged positions: match candidate.current_stage against the columns'
 * stage names (then slugs); empty current_stage falls back to the legacy
 * status field and finally the first stage. Legacy positions: match the
 * normalized application status.
 *
 * @param {string} appStatus     - Application-level status field
 * @param {Object} candidate     - Candidate object (may be undefined)
 * @param {Object} jobPosition   - The job position object
 * @param {Array}  stageColumns  - Stage columns from getBoardColumns (kind 'stage')
 * @returns {Object|string|null} The matching column; the raw unmatched stage/status
 *   name (caller may surface it as an extra column); or null when the candidate has
 *   no pipeline column (legacy candidates sitting on a decision status).
 */
export const resolveCandidateStageColumn = (appStatus, candidate, jobPosition, stageColumns) => {
    const custom = hasValidStages(jobPosition);
    const currentStage = candidate?.current_stage;

    if (custom && currentStage) {
        const slug = stageNameToSlug(currentStage);
        const col = stageColumns.find(c => c.stageName === currentStage)
            || stageColumns.find(c => c.slug === slug);
        return col || currentStage;
    }

    const status = normalizeStatusForJobPosition(appStatus, jobPosition);
    if (custom && DECISION_STATUSES.includes(status)) {
        // A decision doesn't imply a pipeline position; empty current_stage
        // means first stage by convention.
        return stageColumns.find(c => !c.isExtra) || null;
    }
    const col = stageColumns.find(c => c.status === status);
    if (col) return col;
    if (custom) return stageColumns.find(c => !c.isExtra) || null;
    return DECISION_STATUSES.includes(status) ? null : (status || null);
};

/**
 * Build the kanban board columns for a job position.
 *
 * Stage columns come from the position's ordered stages (custom) or its
 * include flags (legacy) — including the final Hire stage, which is a real
 * pipeline stage even though its filter status is 'approved'. Stage values
 * found on candidates but absent from the position config (renamed/removed
 * stages, statuses of disabled legacy stages) become isExtra columns so no
 * candidate silently drops off the board. Decision columns are appended last.
 *
 * Column shape: { key, kind: 'stage'|'decision', label, stageName, slug,
 * status, special, isExtra? } — key is the unique identity (statuses collide:
 * a Hire stage and the Approved decision both map to status 'approved').
 */
export const getBoardColumns = (jobPosition, applications = [], candidates = {}) => {
    const custom = hasValidStages(jobPosition);
    const stageColumns = custom
        ? getOrderedStages(jobPosition).map(stage => ({
            key: `stage:${stage.slug}`,
            kind: 'stage',
            label: stage.stage_name,
            stageName: stage.stage_name,
            slug: stage.slug,
            status: getStageStatusFilter(stage),
            special: stage.special || '',
        }))
        : getAvailableStageStatuses(jobPosition)
            .filter(s => !s.isSystemStatus && s.status !== 'approved')
            .map(s => ({
                key: `stage:${s.slug}`,
                kind: 'stage',
                label: s.label,
                stageName: '',
                slug: s.slug,
                status: s.status,
                special: '',
            }));

    const extras = [];
    const knownKeys = new Set(stageColumns.map(c => c.key));
    (applications || []).forEach(app => {
        const resolved = resolveCandidateStageColumn(app?.status, candidates?.[app?.user_id], jobPosition, stageColumns);
        if (typeof resolved !== 'string') return;
        const slug = stageNameToSlug(resolved) || resolved;
        const key = `stage:${slug}`;
        if (knownKeys.has(key)) return;
        knownKeys.add(key);
        extras.push({
            key,
            kind: 'stage',
            isExtra: true,
            label: custom ? resolved : getStatusDisplayLabel(resolved, jobPosition),
            stageName: custom ? resolved : '',
            slug,
            status: custom ? slug : resolved,
            special: '',
        });
    });

    const decisions = [
        { status: 'approved', label: 'Approved' },
        { status: 'disapproved', label: 'Disapproved' },
        { status: 'ban', label: 'Banned' },
    ].map(d => ({
        key: `decision:${d.status}`,
        kind: 'decision',
        label: d.label,
        stageName: '',
        slug: d.status,
        status: d.status,
        special: '',
    }));

    return [...stageColumns, ...extras, ...decisions];
};

const jobPositionNavigation = {
    stageNameToSlug,
    findStageBySlug,
    hasValidStages,
    getOrderedStages,
    getStageRoute,
    getJobPositionCurrentRoute,
    getJobPositionCurrentStage,
    navigateToJobPositionCurrentState,
    getStageStatusFilter,
    getStageDisplayConfig,
    getFirstStageStatus,
    normalizeStatusForJobPosition,
    getVisibleStatusesForStage,
    isCandidateVisibleOnStage,
    getAvailableStageStatuses,
    getStatusDisplayLabel,
    getStatusBadgeConfig,
    getNextStageInfo,
    shouldUseAdvanceEndpoint,
    getLegacyNextStatus,
    getNextStageStatus,
    shouldUseStageFinishEndpoint,
    getStageSlug,
    findStageBySpecialType,
    isStageCompleted,
    getCurrentStage,
    arePreviousStagesCompleted,
    getNextStageRoute,
    getStageNamesForFiltering,
    getFirstStageName,
    getEffectiveStatus,
    DECISION_STATUSES,
    resolveCandidateStageColumn,
    getBoardColumns
};

export default jobPositionNavigation;
